import React from "react";
import { getOmpSearchString } from "@src/api/main/OMPTypes";
import { divisionServices, type ApiDivisionFilter, type Division } from "@src/api/dictionaries/divisions";
import { formatDivisionText } from "@src/components/business-data-display";
import { PickerOptionCard } from "@shared/ui/picker-option";
import { BusinessSearchSelect, type BusinessSearchSelectProps } from "../BusinessSearchSelect";

export type DivisionSearchSelectProps = Omit<
    BusinessSearchSelectProps<Division, number>,
    "queryKeyBase" | "loadOptions" | "loadById" | "getId" | "getOptionLabel" | "getSelectedLabel"
> & {
    /** Доп. фильтр API (например `AvailableFor: "Create"`). */
    flt?: ApiDivisionFilter;
};

function resolveDivisionCardTitle(item: Division): string {
    const divTypeName = item.DivisionType?.name ?? "<неизвестный тип>";
    const primaryText = formatDivisionText(item, null);

    if (primaryText != null && primaryText !== "") {
        return `<${divTypeName}> ${primaryText}`;
    }
    return divTypeName;
}

/**
 * SearchSelect для одиночного выбора подразделения по коду ({@link Division}). \
 * Popover — компактный `formatDivisionText`; fullscreen — карточка Sign + Name / WSCode.
 */
export function DivisionSearchSelect({
    label = "Подразделение",
    flt,
    minSearchLength = 2,
    debounceDelay = 500,
    searchPlaceholder = "Обозначение подразделения",
    value,
    onChange,
    optionSlots,
    ...rest
}: DivisionSearchSelectProps) {
    return (
        <BusinessSearchSelect<Division, number>
            queryKeyBase={["searchSelect", "division", flt]}
            loadOptions={async (query) => {
                const result = await divisionServices.load({
                    include: ["DivisionType"],
                    flt: {
                        ...flt,
                        Sign: getOmpSearchString(query, { fullTextSearch: true }),
                    },
                });
                return result.items ?? [];
            }}
            loadById={async (code) => {
                const result = await divisionServices.load({
                    include: ["DivisionType"],
                    flt: { ...flt, Codes: [code] },
                    limit: 1,
                    offset: 0,
                });
                return result.items?.[0] ?? null;
            }}
            getId={(item) => item.Code}
            getOptionLabel={(item) => formatDivisionText(item, null)}
            getSelectedLabel={(item) => formatDivisionText(item, null)}
            value={value}
            onChange={onChange}
            label={label}
            searchPlaceholder={searchPlaceholder}
            minSearchLength={minSearchLength}
            debounceDelay={debounceDelay}
            {...rest}
            optionSlots={{
                ...optionSlots,
                fullscreen:
                    optionSlots?.fullscreen ??
                    ((item) => {
                        const title = resolveDivisionCardTitle(item);
                        const name = item.Name?.trim();
                        const wsCode = item.WSCode?.trim();
                        return (
                            <PickerOptionCard
                                title={title}
                                detailsLayout="inline"
                                details={[
                                    {
                                        label: "Наименование",
                                        value: name != null && name !== "" && name !== title ? name : undefined,
                                    },
                                    {
                                        label: "Цех. код",
                                        value: wsCode != null && wsCode !== "" && wsCode !== title ? wsCode : undefined,
                                    },
                                ]}
                            />
                        );
                    }),
            }}
        />
    );
}
